import { z } from 'zod';

import { PERMISSION } from '../../access-control/permission';
import { StaffRole } from '../../access-control/staff';
import { makeEndpoint } from '../common';
import { type OrganizationRole } from '../organization/organization';
import { BrandMembershipRole } from '../user/user';

export enum AccessPrincipal {
  USER = 'user',
  STAFF = 'staff',
  BRAND_KEY = 'brand-key',
  ORGANIZATION_KEY = 'organization-key',
}

const PermissionListSchema = z.array(z.nativeEnum(PERMISSION));

export const AccessMeBrandSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid().nullable(),
  role: z.nativeEnum(BrandMembershipRole).optional(),
  permissions: PermissionListSchema,
});

// Spelled out rather than z.nativeEnum(OrganizationRole): organization.ts sits in an import
// cycle that fails to evaluate outside the bundled package, and this module must stay
// loadable on its own. `satisfies` keeps every literal a real OrganizationRole value.
const ORGANIZATION_ROLES = ['admin'] as const satisfies ReadonlyArray<`${OrganizationRole}`>;

export const AccessMeOrganizationSchema = z.object({
  id: z.string().uuid(),
  role: z.enum(ORGANIZATION_ROLES).optional(),
  permissions: PermissionListSchema,
  brandIds: z.array(z.string().uuid()),
});

export const AccessMeStaffSchema = z.object({
  role: z.nativeEnum(StaffRole),
  permissions: PermissionListSchema,
});

/**
 * Describes a caller for the API Gateway authorizer. `permissions` are effective: the
 * membership list plus the bundle the role grants, as `AccessControl.can()` computes them.
 *
 * `principalId` is the user id for `user` and `staff`, the API key id for the key kinds.
 * Resolving a brand: a `brands[]` entry wins; otherwise the `organizations[]` entry whose
 * `brandIds` contains it (entries are disjoint, the nearest membership up the tree owns the
 * brand). A `staff` principal reaches every brand and organization with `staff.permissions`.
 */
export const GetAccessMeResponseSchema = z.object({
  principal: z.nativeEnum(AccessPrincipal),
  principalId: z.string().uuid(),
  staff: AccessMeStaffSchema.optional(),
  brands: z.array(AccessMeBrandSchema),
  organizations: z.array(AccessMeOrganizationSchema),
});

export type GetAccessMeResponse = z.infer<typeof GetAccessMeResponseSchema>;
export type AccessMeBrand = z.infer<typeof AccessMeBrandSchema>;
export type AccessMeOrganization = z.infer<typeof AccessMeOrganizationSchema>;

export const GetAccessMeEndpoint = makeEndpoint({
  method: 'GET',
  url: () => 'v1/access/me',
  requestSchema: z.void(),
  responseSchema: GetAccessMeResponseSchema,
  title: 'Describe the caller',
  description: 'Who the bearer JWT or secret API key is and what it may do, with effective permissions.',
});
